import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  ChevronDown,
  Plus,
  FlaskConical,
  Key,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  NEW_PORTFOLIO_KEY,
  PORTFOLIO_EXPANDED_PREFIX,
} from "@/constants/storage";

interface SidebarProps {
  isCollapsed: boolean;
  portfolios: Array<{ slug: string; name: string }>;
}

function getCurrentSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/\/portfolio\/([^/]+)/);
  return match ? match[1] : null;
}

export function Sidebar({ isCollapsed, portfolios }: SidebarProps) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const location = useLocation();

  const currentSlug = getCurrentSlugFromPath(location.pathname);

  const isActive = (slug: string, section?: string) => {
    const base = `/portfolio/${slug}`;
    return section ? location.pathname === `${base}/${section}` : location.pathname === base;
  };

  const newPortfolioSlug = (() => {
    const slug = localStorage.getItem(NEW_PORTFOLIO_KEY);
    return slug && portfolios.some(p => p.slug === slug) ? slug : null;
  })();

  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const [prevSlug, setPrevSlug] = useState<string | null>(null);

  // Restore expanded state from localStorage once when portfolios first load
  if (portfolios.length > 0 && !initialized) {
    setInitialized(true);
    const expanded = new Set<string>();
    portfolios.forEach((p) => {
      const stored = localStorage.getItem(`${PORTFOLIO_EXPANDED_PREFIX}${p.slug}`);
      if (stored === "true") {
        expanded.add(p.slug);
      }
    });
    if (currentSlug) {
      expanded.add(currentSlug);
      localStorage.setItem(`${PORTFOLIO_EXPANDED_PREFIX}${currentSlug}`, "true");
    }
    setExpandedSlugs(expanded);
  }

  // Auto-expand when navigating to a portfolio
  if (currentSlug !== prevSlug) {
    setPrevSlug(currentSlug);
    if (currentSlug && initialized && !expandedSlugs.has(currentSlug)) {
      const next = new Set(expandedSlugs);
      next.add(currentSlug);
      localStorage.setItem(`${PORTFOLIO_EXPANDED_PREFIX}${currentSlug}`, "true");
      setExpandedSlugs(next);
    }
  }

  useEffect(() => {
    if (newPortfolioSlug && currentSlug === newPortfolioSlug) {
      localStorage.removeItem(NEW_PORTFOLIO_KEY);
    }
  }, [newPortfolioSlug, currentSlug]);

  const togglePortfolioExpanded = (slug: string) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
        localStorage.setItem(`${PORTFOLIO_EXPANDED_PREFIX}${slug}`, "false");
      } else {
        next.add(slug);
        localStorage.setItem(`${PORTFOLIO_EXPANDED_PREFIX}${slug}`, "true");
      }
      return next;
    });
  };

  const isDashboardActive = location.pathname === "/dashboard";
  const isRiskCheckActive = location.pathname.startsWith("/risk-checks");
  const isApiKeysActive = location.pathname === "/api-keys";
  const isPortfolioPath = (slug: string) => location.pathname.startsWith(`/portfolio/${slug}`);

  const handleCreatePortfolio = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/portfolio/new");
  };

  if (isCollapsed) {
    return (
      <nav className="flex flex-col items-center pt-1 space-y-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => navigate("/dashboard")}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer",
                isDashboardActive
                  ? "bg-sidebar-primary/10 text-sidebar-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <LayoutDashboard size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t("sidebar.dashboard", "Dashboard")}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => navigate("/risk-checks")}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer",
                isRiskCheckActive
                  ? "bg-sidebar-primary/10 text-sidebar-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <FlaskConical size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t("sidebar.riskCheck", "Risk Check")}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => navigate("/api-keys")}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer",
                isApiKeysActive
                  ? "bg-sidebar-primary/10 text-sidebar-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <Key size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t("sidebar.apiKeys", "API Keys")}</TooltipContent>
        </Tooltip>

        {portfolios.map((portfolio) => (
          <Tooltip key={portfolio.slug}>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate(`/portfolio/${portfolio.slug}`)}
                className={cn(
                  "w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer",
                  isPortfolioPath(portfolio.slug)
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Briefcase size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{portfolio.name}</TooltipContent>
          </Tooltip>
        ))}
      </nav>
    );
  }

  return (
    <nav className="flex flex-col space-y-1">
      {/* Dashboard */}
      <button
        onClick={() => navigate("/dashboard")}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
          isDashboardActive
            ? "bg-sidebar-primary/10 text-sidebar-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        <LayoutDashboard size={18} />
        <span>{t("sidebar.dashboard", "Dashboard")}</span>
      </button>

      {/* Risk Check */}
      <button
        onClick={() => navigate("/risk-checks")}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
          isRiskCheckActive
            ? "bg-sidebar-primary/10 text-sidebar-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        <FlaskConical size={18} />
        <span>{t("sidebar.riskCheck", "Risk Check")}</span>
      </button>

      {/* API Keys */}
      <button
        onClick={() => navigate("/api-keys")}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
          isApiKeysActive
            ? "bg-sidebar-primary/10 text-sidebar-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        <Key size={18} />
        <span>{t("sidebar.apiKeys", "API Keys")}</span>
      </button>

      {/* Portfolios Section */}
      <div className="pt-4">
        {/* Section Header */}
        <div className="group pb-2">
          <div className="flex items-center justify-between px-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("sidebar.portfolios", "Portfolios")}
            </span>
            <button
              className={cn(
                "w-5 h-5 rounded flex items-center justify-center transition-all",
                "text-muted-foreground hover:text-foreground hover:bg-muted",
                "opacity-0 group-hover:opacity-100"
              )}
              onClick={handleCreatePortfolio}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Portfolio List */}
        {portfolios.length === 0 ? (
          <div className="px-4 py-2 text-sm text-muted-foreground">
            {t("portfolio.empty", "No portfolios yet")}
          </div>
        ) : (
          <div className="space-y-0.5">
            {portfolios.map((portfolio) => (
              <Collapsible
                key={portfolio.slug}
                open={expandedSlugs.has(portfolio.slug)}
              >
                {/* Portfolio Toggle */}
                <CollapsibleTrigger asChild>
                  <button
                    onClick={() => togglePortfolioExpanded(portfolio.slug)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer",
                      "transition-colors",
                      isPortfolioPath(portfolio.slug)
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Briefcase size={18} className="shrink-0" />
                    <span className="flex-1 text-left truncate">{portfolio.name}</span>
                    {newPortfolioSlug === portfolio.slug && (
                      <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                    )}
                    <ChevronDown
                      size={16}
                      className={cn(
                        "shrink-0 transition-transform",
                        expandedSlugs.has(portfolio.slug) && "rotate-180"
                      )}
                    />
                  </button>
                </CollapsibleTrigger>

                {/* Sub-Items */}
                <CollapsibleContent>
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-border pl-3">
                    {/* Analytics */}
                    <div>
                      <button
                        onClick={() => navigate(`/portfolio/${portfolio.slug}/analytics`)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                          isActive(portfolio.slug, "analytics")
                            ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {t("sidebar.analytics", "Analytics")}
                      </button>
                      {/* Tax Summary (nested) */}
                      <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border/50 pl-2">
                        <button
                          onClick={() => navigate(`/portfolio/${portfolio.slug}/analytics/taxes`)}
                          className={cn(
                            "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                            location.pathname === `/portfolio/${portfolio.slug}/analytics/taxes`
                              ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          {t("sidebar.taxes", "Tax Summary")}
                        </button>
                      </div>
                    </div>

                    {/* Risk Dashboard */}
                    <button
                      onClick={() => navigate(`/portfolio/${portfolio.slug}/dashboard`)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                        isActive(portfolio.slug, "dashboard")
                          ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {t("sidebar.riskDashboard", "Risk Dashboard")}
                    </button>

                    {/* Stress Tests */}
                    <div>
                      <button
                        onClick={() => navigate(`/portfolio/${portfolio.slug}/risks`)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                          isActive(portfolio.slug, "risks")
                            ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {t("sidebar.risks", "Stress Tests")}
                      </button>
                      {/* Simulator (nested) */}
                      <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border/50 pl-2">
                        <button
                          onClick={() => navigate(`/portfolio/${portfolio.slug}/risks/simulator`)}
                          className={cn(
                            "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                            location.pathname === `/portfolio/${portfolio.slug}/risks/simulator`
                              ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          {t("sidebar.simulator", "Simulator")}
                        </button>
                      </div>
                    </div>

                    {/* Holding Summary */}
                    <button
                      onClick={() => navigate(`/portfolio/${portfolio.slug}`)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                        isActive(portfolio.slug)
                          ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {t("sidebar.holdingSummary", "Holding Summary")}
                    </button>

                    {/* Transactions */}
                    <button
                      onClick={() => navigate(`/portfolio/${portfolio.slug}/transactions`)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                        isActive(portfolio.slug, "transactions")
                          ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {t("sidebar.transactions", "Transactions")}
                    </button>

                    {/* Settings */}
                    <button
                      onClick={() => navigate(`/portfolio/${portfolio.slug}/settings`)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                        isActive(portfolio.slug, "settings")
                          ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {t("sidebar.settings", "Settings")}
                    </button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
